import React, { useMemo, useState, useRef, useEffect } from 'react';
import Select, { StylesConfig, SelectInstance } from 'react-select';
import { useDarkMode } from '../../hooks/useDarkMode';

interface Option {
  value: string;
  label: string;
  secondaryLabel?: string;
}

interface CellSelectProps<T> {
  items: T[];
  value: string;
  onChange: (item: T | null) => void;
  getOptionValue: (item: T) => string;
  getOptionLabel: (item: T) => string;
  getSecondaryLabel?: (item: T) => string;
  placeholder?: string;
  isDisabled?: boolean;
  searchBySecondaryLabel?: boolean;
  /** Disable client-side filtering when using server-side search */
  disableClientFilter?: boolean;
  autoFocus?: boolean;
  defaultMenuIsOpen?: boolean;
  onBlur?: () => void;
  initialInputValue?: string;
  onInputChange?: (value: string) => void;
  onFocus?: () => void;
  isLoading?: boolean;
}

const getCellStyles = (isDark: boolean): StylesConfig<Option, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '30px', // Reduced to match table cell
    height: '30px',    // Reduced to match table cell
    border: 'none',
    borderRadius: 0,
    backgroundColor: state.isFocused 
      ? (isDark ? 'rgb(55 65 81)' : 'rgb(239 246 255)') 
      : 'transparent',
    boxShadow: 'none',
    '&:hover': {
      border: 'none',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    height: '30px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: '14px',
    color: isDark ? '#f9fafb' : '#111827',
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '14px',
    color: isDark ? '#f9fafb' : '#111827',
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '14px',
    color: '#9ca3af',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '30px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px',
    display: 'none',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px',
    color: isDark ? '#9ca3af' : '#6b7280',
  }),
  menu: (base) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: isDark ? '#1f2937' : 'white',
    border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
    minWidth: '280px',
    width: 'max-content',
    maxWidth: '400px',
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: '250px',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    padding: '8px 12px',
    backgroundColor: state.isFocused 
      ? (isDark ? '#374151' : 'rgb(239 246 255)') 
      : (isDark ? '#1f2937' : 'white'),
    color: isDark ? '#f9fafb' : '#111827',
    '&:active': {
      backgroundColor: isDark ? '#4b5563' : 'rgb(219 234 254)',
    },
  }),
});

export function CellSelect<T>({
  items,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  getSecondaryLabel,
  placeholder = '',
  isDisabled = false,
  searchBySecondaryLabel = false,
  disableClientFilter = false,
  autoFocus = false,
  defaultMenuIsOpen = false,
  onBlur,
  initialInputValue = '',
  onInputChange: onInputChangeExternal,
  onFocus: onFocusExternal,
  isLoading = false,
}: CellSelectProps<T>) {
  const { isDarkMode: isDark } = useDarkMode();
  const [inputValue, setInputValue] = useState(initialInputValue);
  const selectRef = useRef<SelectInstance<Option, false>>(null);

  // Simple manual focus without the complex scroll hacks
  useEffect(() => {
    if (autoFocus && selectRef.current) {
      selectRef.current.focus();
    }
  }, [autoFocus]);

  const cellStyles = useMemo(() => getCellStyles(isDark), [isDark]);

  const options: Option[] = useMemo(() => 
    items.map(item => ({
      value: getOptionValue(item),
      label: getOptionLabel(item),
      secondaryLabel: getSecondaryLabel?.(item),
    })),
    [items, getOptionValue, getOptionLabel, getSecondaryLabel]
  );

  const selectedOption = useMemo(() => 
    options.find(opt => opt.value === value) || null,
    [options, value]
  );

  const handleChange = (option: Option | null) => {
    if (option) {
      const item = items.find(i => getOptionValue(i) === option.value);
      onChange(item || null);
    } else {
      onChange(null);
    }
  };

  const handleBlur = () => {
    onBlur?.();
  };

  const formatOptionLabel = (option: Option) => (
    <div>
      <div>{option.label}</div>
      {option.secondaryLabel && (
        <div className="text-xs text-gray-500 dark:text-gray-400">{option.secondaryLabel}</div>
      )}
    </div>
  );

  const filterOption = disableClientFilter
    ? () => true // Show all options when using server-side filtering
    : searchBySecondaryLabel
    ? (option: { data: Option }, inputValue: string) => {
        const searchLower = inputValue.toLowerCase();
        return (
          option.data.label.toLowerCase().includes(searchLower) ||
          (option.data.secondaryLabel?.toLowerCase().includes(searchLower) ?? false)
        );
      }
    : undefined;

  return (
    <Select<Option, false>
      ref={selectRef}
      value={selectedOption}
      onChange={handleChange}
      options={options}
      styles={cellStyles}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable
      isSearchable
      formatOptionLabel={formatOptionLabel}
      filterOption={filterOption}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuPlacement="auto"
      // CRITICAL: Prevent react-select from scrolling page to view menu
      menuShouldScrollIntoView={false}
      // CRITICAL: Don't block scroll, but don't close on scroll either (unless desired)
      closeMenuOnScroll={false}
      defaultMenuIsOpen={defaultMenuIsOpen}
      onBlur={handleBlur}
      // CRITICAL: Prevent input blur from triggering page scroll
      blurInputOnSelect={false}
      openMenuOnFocus={autoFocus}
      inputValue={inputValue}
      onInputChange={(newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          setInputValue(newValue);
          onInputChangeExternal?.(newValue);
        }
      }}
      onFocus={() => onFocusExternal?.()}
      isLoading={isLoading}
    />
  );
}

export default CellSelect;
